import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { NotificationList } from '../components/notifications/NotificationList';
import {
  NotificationFilters,
  NotificationFilterState,
} from '../components/notifications/NotificationFilters';
import { UserNotification } from '../types/user';

/**
 * NotificationCenter Page
 * Full-page notification management interface
 */
const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    archiveNotification,
    unarchiveNotification,
    deleteNotification,
  } = useNotifications();

  // Filter state
  const [filters, setFilters] = useState<NotificationFilterState>({
    readStatus: 'all',
    archived: 'active',
  });

  // Apply filters to notifications
  const filteredNotifications = useMemo(() => {
    let filtered = [...notifications];

    // Filter by read status
    if (filters.readStatus === 'read') {
      filtered = filtered.filter((n) => n.read);
    } else if (filters.readStatus === 'unread') {
      filtered = filtered.filter((n) => !n.read);
    }

    // Filter by archived status
    if (filters.archived === 'archived') {
      filtered = filtered.filter((n) => n.archived);
    } else if (filters.archived === 'active') {
      filtered = filtered.filter((n) => !n.archived);
    }

    // Filter by type
    if (filters.type) {
      filtered = filtered.filter((n) => n.type === filters.type);
    }

    // Filter by priority
    if (filters.priority) {
      filtered = filtered.filter((n) => n.priority === filters.priority);
    }

    return filtered;
  }, [notifications, filters]);

  // Calculate counts for filter badges
  const counts = useMemo(() => {
    const activeNotifications = notifications.filter((n) => !n.archived);
    return {
      all: notifications.length,
      unread: notifications.filter((n) => !n.read && !n.archived).length,
      archived: notifications.filter((n) => n.archived).length,
    };
  }, [notifications]);

  const handleRefresh = () => {
    // Notifications are real-time, so this is mostly for visual feedback
    // Could add a manual refresh method to NotificationContext if needed
    window.location.reload();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Notifications
            </Typography>
            <Box>
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Notification Settings">
                <IconButton onClick={() => navigate('/notifications/preferences')}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Grid>

        {/* Filters Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper>
            <NotificationFilters
              filters={filters}
              onChange={setFilters}
              counts={counts}
            />
          </Paper>
        </Grid>

        {/* Notification List */}
        <Grid item xs={12} md={9}>
          <Paper>
            <NotificationList
              notifications={filteredNotifications}
              loading={loading}
              onMarkAsRead={markAsRead}
              onMarkAsUnread={markAsUnread}
              onArchive={archiveNotification}
              onUnarchive={unarchiveNotification}
              onDelete={deleteNotification}
              onMarkAllAsRead={
                filters.readStatus !== 'read' ? markAllAsRead : undefined
              }
              emptyMessage={
                filters.archived === 'archived'
                  ? 'No archived notifications'
                  : filters.readStatus === 'unread'
                  ? 'No unread notifications'
                  : 'No notifications'
              }
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default NotificationCenter;
